import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TabsContent } from "@/components/ui/tabs";
import { HelpCircle } from "lucide-react";
import { useState } from "react";
import type { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import type { DriverFormValues } from "./form";

interface DriverVehiclesTabProps {
  form: UseFormReturn<DriverFormValues>;
  fields: UseFieldArrayReturn<DriverFormValues, "vehicles">["fields"];
}

const DriverVehiclesTab = ({ fields, form }: DriverVehiclesTabProps) => {
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);

    const newPermissionValue = checked ? 26 : 0;
    const newDriveValue = checked ? 1 : 0;

    fields.forEach((_, index) => {
      form.setValue(`vehicles.${index}.permission`, newPermissionValue);
      form.setValue(`permissionsToDrive.${index}.status`, newDriveValue);
    });
  };

  return (
    <TabsContent value="vehicles" className="space-y-6 mt-4">
      <div className="space-y-4">
        <div className="p-4 rounded-md">
          <label htmlFor="">Permitir tudo</label>
          <Switch
            id="select-all"
            className="ml-2 bg-foreground"
            checked={selectAll}
            onCheckedChange={handleSelectAll}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Veículo</TableHead>
                <TableHead>Monitorar</TableHead>
                <TableHead>
                  <div className="flex items-center gap-1 relative group">
                    Atuadores
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    <div className="absolute z-10 invisible group-hover:visible bg-black text-white text-xs rounded p-2 -right-4 top-6 w-64 shadow-lg">
                      Permissão para utilizar os atuadores instalados no
                      veículo, como bloqueio, acionamento de travas, sirene, etc
                    </div>
                  </div>
                </TableHead>
                <TableHead>Configurar</TableHead>
                <TableHead>Dirigir</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((field, index) => (
                <TableRow key={field.id}>
                  <TableCell>
                    {field.plate} - {field.title}
                  </TableCell>
                  <TableCell>
                    <FormField
                      control={form.control}
                      name={`vehicles.${index}.permission`}
                      render={({ field: { value = 0, onChange } }) => (
                        <FormItem>
                          <FormControl>
                            <Switch
                              className="bg-foreground"
                              checked={!!(value & 2)}
                              onCheckedChange={(checked) =>
                                onChange(checked ? value | 2 : value & ~2)
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      control={form.control}
                      name={`vehicles.${index}.permission`}
                      render={({ field: { value = 0, onChange } }) => (
                        <FormItem>
                          <FormControl>
                            <Switch
                              className="bg-foreground"
                              checked={!!(value & 8)}
                              onCheckedChange={(checked) =>
                                onChange(checked ? value | 8 : value & ~8)
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      control={form.control}
                      name={`vehicles.${index}.permission`}
                      render={({ field: { value = 0, onChange } }) => (
                        <FormItem>
                          <FormControl>
                            <Switch
                              className="bg-foreground"
                              checked={!!(value & 16)}
                              onCheckedChange={(checked) =>
                                onChange(checked ? value | 16 : value & ~16)
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      control={form.control}
                      name={`permissionsToDrive.${index}.status`}
                      render={({ field: { value = 0, onChange } }) => {
                        const originalStatus = field.authorization_to_drive;

                        const getStatusLabel = (status: number): string => {
                          const statusLabels: Record<number, string> = {
                            0: "",
                            1: "Habilitado",
                            2: "Habilitando...",
                            3: "Pendente",
                          };
                          return statusLabels[status] || "";
                        };

                        const getSwitchColor = (status: number) => {
                          switch (status) {
                            case 1:
                              return "bg-green-500";
                            case 2:
                              return "bg-orange-300";
                            case 3:
                              return "bg-yellow-500";
                            default:
                              return "bg-foreground";
                          }
                        };

                        return (
                          <FormItem>
                            <FormControl>
                              <div className="flex items-center">
                                <Switch
                                  className={getSwitchColor(originalStatus)}
                                  checked={!!value}
                                  onCheckedChange={(checked) =>
                                    onChange(checked ? 1 : 0)
                                  }
                                />
                                <span className="ml-2 text-sm">
                                  {getStatusLabel(originalStatus)}
                                </span>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </TabsContent>
  );
};

export default DriverVehiclesTab;
